import React, { useCallback, useEffect, useMemo, useState } from "react";
import simpleFarmService from "../../../shared/api/farming/simpleFarmService";
import { Button, Field, Notice, PageTitle, Stat, TableShell, card, input, td, th, todayIso } from "./farmUi";

export default function FarmingTaskPage() {
  const [date, setDate] = useState(todayIso());
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const pending = useMemo(() => works.filter((w) => w.status === "PENDING").length, [works]);
  const done = useMemo(() => works.filter((w) => w.status === "DONE").length, [works]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setWorks(await simpleFarmService.todayWorks.list(date)); } catch (e) { setError(simpleFarmService._apiMsg(e)); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  async function generate() {
    setError(""); setMessage("");
    try { const data = await simpleFarmService.todayWorks.generate(date); setWorks(data); setMessage("Today's work generated from active schedules."); } catch (e) { setError(simpleFarmService._apiMsg(e)); }
  }

  async function complete(w) {
    const remarks = window.prompt("Remarks (optional)", w.remarks || "") || "";
    try { await simpleFarmService.todayWorks.complete(w.id, remarks); setMessage(w.workType === "FERTILIZER" ? "Marked Yes. Fertilizer stock reduced." : "Irrigation marked completed."); await load(); } catch (e) { setError(simpleFarmService._apiMsg(e)); }
  }

  async function skip(w) {
    const remarks = window.prompt("Reason / remarks (optional)", w.remarks || "") || "";
    try { await simpleFarmService.todayWorks.skip(w.id, remarks); setMessage("Work marked No / skipped."); await load(); } catch (e) { setError(simpleFarmService._apiMsg(e)); }
  }

  return <div style={{ color: "#f8fafc" }}>
    <PageTitle title="Today's Work" subtitle="Generate work from schedules for a date. Mark Yes to complete. If it is a fertilizer work, fertilizer stock reduces only when marked Yes.">
      <div style={{ display: "flex", gap: 10 }}><Stat value={works.length} labelText="Total" /><Stat value={pending} labelText="Pending" tone={pending ? "bad" : "good"} /><Stat value={done} labelText="Done" tone="good" /></div>
    </PageTitle>
    {error && <Notice type="error">{error}</Notice>}{message && <Notice type="success">{message}</Notice>}

    <div style={{ ...card, marginBottom: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,260px) auto auto", gap: 12, alignItems: "end" }}>
        <Field labelText="Work Date"><input style={input} type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Button variant="pink" onClick={generate}>Generate Works</Button>
        <Button variant="muted" onClick={load}>{loading ? "Loading..." : "Refresh"}</Button>
      </div>
      <div style={{ marginTop: 12, color: "#94a3b8", fontSize: 13 }}>Tip: Create schedules first. Then click Generate Works for today. Generated entries will not duplicate for the same schedule/date.</div>
    </div>

    <div style={card}>
      <TableShell><thead><tr><th style={th}>Zone</th><th style={th}>Crop</th><th style={th}>Valve</th><th style={th}>Work</th><th style={th}>Quantity</th><th style={th}>Status</th><th style={th}>Yes / No</th></tr></thead><tbody>{works.map((w) => <tr key={w.id}><td style={td}><b>{w.zoneName || "-"}</b></td><td style={td}>{w.cropName || "-"}</td><td style={td}>{w.valveNumber || "All"}</td><td style={td}>{w.workType === "FERTILIZER" ? <><b>{w.fertilizerName}</b><div style={{ color: "#94a3b8" }}>FERTILIZER</div></> : <b>IRRIGATION</b>}</td><td style={td}>{w.workType === "FERTILIZER" ? `${w.quantity || 0} ${w.unit || ""}` : "-"}</td><td style={{ ...td, color: w.status === "DONE" ? "#86efac" : w.status === "SKIPPED" ? "#facc15" : "#fb7185", fontWeight: 900 }}>{w.status}</td><td style={td}>{w.status === "PENDING" ? <div style={{ display: "flex", gap: 8 }}><Button onClick={() => complete(w)}>Yes</Button><Button variant="danger" onClick={() => skip(w)}>No</Button></div> : <span style={{ color: "#94a3b8" }}>{w.remarks || "Completed"}</span>}</td></tr>)}{!works.length && <tr><td style={td} colSpan="7">No work found. Click Generate Works.</td></tr>}</tbody></TableShell>
    </div>
  </div>;
}
